import {
    type ReactNode,
    useEffect,
} from 'react';

import { ThemeToggle } from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';

/* ───────────────────────── data ───────────────────────── */

const SECTIONS = [
    { id: 'info-we-collect', title: '1. Information We Collect' },
    { id: 'how-we-use', title: '2. How We Use Information' },
    { id: 'legal-basis', title: '3. Legal Basis for Processing' },
    { id: 'sharing', title: '4. How We Share Information' },
    { id: 'account-deletion', title: '5. Account Deletion' },
    { id: 'team-data', title: '6. Team and Business Data' },
    { id: 'retention', title: '7. Data Retention' },
    { id: 'security', title: '8. Data Security' },
    { id: 'rights', title: '9. Your Rights and Choices' },
    { id: 'children', title: "10. Children's Privacy" },
    { id: 'transfers', title: '11. International Data Transfers' },
    { id: 'thirdparty', title: '12. Third-Party Services' },
    { id: 'changes', title: '13. Changes to This Policy' },
    { id: 'contact', title: '14. Contact Us' },
];

/* ───────────────────────── layout primitives ───────────────────────── */

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
    return (
        <section
            id={id}
            className={cn(
                'scroll-mt-24 border-t border-border/70 pt-10 first:border-t-0 first:pt-0',
            )}
        >
            <h2 className="text-xl font-semibold tracking-tight text-foreground">{title}</h2>
            <div className="mt-4 space-y-4 text-[0.95rem] leading-relaxed text-muted-foreground">
                {children}
            </div>
        </section>
    );
}

function SubHeading({ children }: { children: ReactNode }) {
    return (
        <h3 className="!mt-6 text-[0.95rem] font-semibold text-foreground">
            {children}
        </h3>
    );
}

function Card({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div className={cn('rounded-2xl border border-border bg-card p-6', className)}>
            {children}
        </div>
    );
}

function DataTable({ rows }: { rows: { category: string; examples: string }[] }) {
    return (
        <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full border-collapse text-left text-[0.9rem]">
                <thead>
                    <tr className="bg-muted">
                        <th className="px-4 py-2.5 font-semibold text-foreground">Category</th>
                        <th className="px-4 py-2.5 font-semibold text-foreground">Examples</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.category} className="border-t border-border">
                            <td className="px-4 py-2.5 align-top font-medium text-foreground">
                                {row.category}
                            </td>
                            <td className="px-4 py-2.5 align-top text-muted-foreground">
                                {row.examples}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ───────────────────────── page ───────────────────────── */

function PrivacyPolicy() {
    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'Privacy Policy | Tipa';
        return () => {
            document.title = previousTitle;
        };
    }, []);

    return (
        <div className="min-h-[100dvh] bg-background text-foreground">
            <header className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur-xl">
                <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5 md:px-8">
                    <span className="text-[1.05rem] font-bold tracking-tight text-foreground">
                        Tipa
                    </span>
                    <ThemeToggle />
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-5 pb-24 pt-10 md:px-8 md:pt-14">
                <div className="mb-10">
                    <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                        Privacy Policy
                    </h1>
                    <p className="mt-3 text-sm text-muted-foreground">
                        App:
                        {' '}
                        <span className="font-medium text-foreground">Tipa</span>
                        <span className="mx-2 text-border">·</span>
                        Effective date:
                        {' '}
                        <span className="font-medium text-foreground">July 19, 2026</span>
                        <span className="mx-2 text-border">·</span>
                        Last updated:
                        {' '}
                        <span className="font-medium text-foreground">July 19, 2026</span>
                    </p>
                </div>

                <div className="space-y-4 text-[0.95rem] leading-relaxed text-muted-foreground">
                    <p>
                        This Privacy Policy explains how
                        {' '}
                        <span className="font-medium text-foreground">Code X Pvt Ltd</span>
                        {' '}
                        (&quot;Code X&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;)
                        collects, uses, stores, and shares information when you use the
                        {' '}
                        <span className="font-medium text-foreground">Tipa</span>
                        {' '}
                        mobile application and its related backend services (together, the
                        &quot;Service&quot;). Tipa is an inventory and stock management
                        application intended for business owners and their team members.
                    </p>
                    <p>
                        By creating an account or otherwise using Tipa, you agree to the
                        collection and use of information as described in this policy. If you
                        do not agree, please do not use the Service.
                    </p>
                </div>

                <nav aria-label="Table of contents" className="mt-8">
                    <Card>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                            Contents
                        </p>
                        <ol className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                            {SECTIONS.map((s) => (
                                <li key={s.id}>
                                    <a
                                        href={`#${s.id}`}
                                        className="text-sm text-primary underline-offset-4 hover:underline"
                                    >
                                        {s.title}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </Card>
                </nav>

                <div className="mt-14 space-y-10">
                    <Section id="info-we-collect" title="1. Information We Collect">
                        <SubHeading>1.1 Account and Profile Information</SubHeading>
                        <p>When you register for and use Tipa, we collect:</p>
                        <DataTable
                            rows={[
                                {
                                    category: 'Account credentials',
                                    examples: 'Email address, username, password (stored in hashed form)',
                                },
                                {
                                    category: 'Profile details',
                                    examples: 'First and last name, phone/contact number, date of birth, profile photo',
                                },
                                {
                                    category: 'Business details',
                                    examples: 'Business name, business type, address, city, state, ward/locality',
                                },
                                {
                                    category: 'Verification data',
                                    examples: 'Email verification codes, password-reset OTPs (short-lived, not retained after use)',
                                },
                                {
                                    category: 'Preferences',
                                    examples: 'Promotional-email opt-in/opt-out, push-notification enabled/disabled',
                                },
                            ]}
                        />

                        <SubHeading>1.2 Inventory and Business Data</SubHeading>
                        <p>
                            To provide the Service&apos;s core functionality, we store the
                            business content you and your team create, including:
                        </p>
                        <ul className="list-disc space-y-1.5 pl-5">
                            <li>
                                Products, categories, units, and storage locations, including
                                product photos
                            </li>
                            <li>
                                Stock levels, pricing, thresholds, and per-location stock
                                records
                            </li>
                            <li>
                                Inventory logs (stock counts, adjustments, and approval
                                history)
                            </li>
                            <li>
                                Team membership, roles (owner, member, supervisor), and approval
                                actions
                            </li>
                        </ul>

                        <SubHeading>1.3 Diagnostic and Technical Information</SubHeading>
                        <p>
                            When the app encounters an error, or to keep the Service running
                            reliably, we may automatically collect:
                        </p>
                        <ul className="list-disc space-y-1.5 pl-5">
                            <li>
                                Crash/error reports: error message, stack trace, the app screen
                                where it occurred
                            </li>
                            <li>App version and device platform (e.g. Android or iOS)</li>
                            <li>
                                Authentication tokens (JWT access/refresh tokens) used to keep you
                                signed in
                            </li>
                            <li>
                                IP address, for rate-limiting and abuse prevention on our
                                servers
                            </li>
                        </ul>

                    </Section>

                    <Section id="how-we-use" title="2. How We Use Information">
                        <p>We use the information described above to:</p>
                        <ul className="list-disc space-y-1.5 pl-5">
                            <li>
                                Create and manage your account, and authenticate you via
                                secure tokens
                            </li>
                            <li>
                                Provide the core inventory management features of the Service
                                (products, stock, teams, approvals, reporting/export)
                            </li>
                            <li>
                                Send transactional communications: email verification codes,
                                password-reset OTPs, and account-related notices
                            </li>
                            <li>
                                Send optional promotional emails and push notifications, where
                                you have opted in
                            </li>
                            <li>
                                Diagnose crashes, fix bugs, and monitor the reliability and
                                performance of the Service
                            </li>
                            <li>
                                Detect, prevent, and investigate fraud, abuse, or security
                                incidents
                            </li>
                            <li>Comply with legal obligations</li>
                        </ul>
                        <p>
                            We do
                            {' '}
                            <span className="font-medium text-foreground">not</span>
                            {' '}
                            sell your personal information, and we do not use your data to
                            serve third-party advertising.
                        </p>
                    </Section>

                    <Section id="legal-basis" title="3. Legal Basis for Processing">
                        <p>
                            Where applicable data protection law requires a legal basis, we
                            rely on:
                        </p>
                        <ul className="list-disc space-y-1.5 pl-5">
                            <li>
                                <span className="font-medium text-foreground">Contractual necessity</span>
                                {' '}
                                — to provide the Service you signed up for
                            </li>
                            <li>
                                <span className="font-medium text-foreground">Consent</span>
                                {' '}
                                — for optional features such as promotional emails and push
                                notifications, which you may withdraw at any time
                            </li>
                            <li>
                                <span className="font-medium text-foreground">Legitimate interests</span>
                                {' '}
                                — to secure the Service, prevent abuse, and improve reliability
                            </li>
                            <li>
                                <span className="font-medium text-foreground">Legal obligation</span>
                                {' '}
                                — where we must retain or disclose information to comply with
                                the law
                            </li>
                        </ul>
                    </Section>

                    <Section id="sharing" title="4. How We Share Information">
                        <p>
                            We do not sell or rent your personal information. We share
                            information only in the following circumstances:
                        </p>
                        <ul className="list-disc space-y-1.5 pl-5">
                            <li>
                                <span className="font-medium text-foreground">Within your team</span>
                                {' '}
                                — inventory, product, and stock data you or your team members
                                create is shared within your team, scoped to the team owner&apos;s
                                account, so that team members and supervisors can collaborate as
                                intended.
                            </li>
                            <li>
                                <span className="font-medium text-foreground">Service providers</span>
                                {' '}
                                — we use trusted infrastructure providers to operate the
                                Service, including cloud/object storage for images and files
                                (S3-compatible storage such as MinIO), and hosting providers for
                                our servers and database. These providers process data only on
                                our instructions and are bound by confidentiality obligations.
                            </li>
                            <li>
                                <span className="font-medium text-foreground">Legal and safety reasons</span>
                                {' '}
                                — we may disclose information if required by law, legal process,
                                or to protect the rights, property, or safety of Code X, our
                                users, or the public.
                            </li>
                            <li>
                                <span className="font-medium text-foreground">Business transfers</span>
                                {' '}
                                — if Code X is involved in a merger, acquisition, or sale of
                                assets, your information may be transferred as part of that
                                transaction, subject to this policy.
                            </li>
                        </ul>
                    </Section>

                    <Section id="account-deletion" title="5. Account Deletion">
                        <p>
                            You may request deletion of your Tipa account at any time, either
                            from within the app or by contacting us using the details in
                            {' '}
                            <a href="#contact" className="text-primary underline-offset-4 hover:underline">
                                Section 14
                            </a>
                            . Before we proceed, we may ask you to confirm your identity or
                            re-enter your password.
                        </p>

                        <Card className="border-destructive/40 bg-destructive/5">
                            <p className="text-[0.95rem] font-semibold text-foreground">
                                Account deletion is permanent and cannot be undone.
                            </p>
                            <p className="mt-2 text-[0.95rem] leading-relaxed text-muted-foreground">
                                Once your account is deleted, your data is permanently removed
                                from our systems and
                                {' '}
                                <span className="font-medium text-foreground">
                                    cannot be recovered or restored
                                </span>
                                {' '}
                                — by you or by us. There is no undo, no grace period, and no
                                way for our support team to bring the data back. If you may need
                                your records later, export them before requesting deletion.
                            </p>
                        </Card>

                        <SubHeading>5.1 What Is Deleted</SubHeading>
                        <p>When your account is deleted, we permanently remove:</p>
                        <ul className="list-disc space-y-1.5 pl-5">
                            <li>
                                Your account credentials, profile details, and profile photo
                            </li>
                            <li>
                                Your business details and preferences (notification and email
                                settings)
                            </li>
                            <li>
                                Your authentication tokens, which immediately signs you out of
                                all devices
                            </li>
                            <li>
                                Where you are a team owner: the products, categories, units,
                                locations, stock records, inventory logs, and uploaded images
                                belonging to your team
                            </li>
                        </ul>

                        <SubHeading>5.2 What May Remain</SubHeading>
                        <p>
                            A small amount of information may survive deletion, and only for
                            limited purposes:
                        </p>
                        <ul className="list-disc space-y-1.5 pl-5">
                            <li>
                                Records we are required to keep to comply with legal, tax,
                                accounting, or security obligations, or to resolve disputes
                            </li>
                            <li>
                                Anonymized or aggregated data that can no longer be linked back
                                to you
                            </li>
                            <li>
                                Inventory records and activity history that belong to a team you
                                joined but do not own, which remain with that team owner
                            </li>
                            <li>
                                Residual copies in encrypted backups, which are overwritten on
                                our normal backup rotation and are not restored to the live
                                Service
                            </li>
                        </ul>

                        <SubHeading>5.3 Deleting a Team Owner Account</SubHeading>
                        <p>
                            If you are a team owner, deleting your account also removes the
                            team&apos;s inventory data and revokes your team members&apos;
                            access to it. This is irreversible and affects everyone on the
                            team, so notify your team and export any needed records first.
                        </p>
                    </Section>

                    <Section id="team-data" title="6. Team and Business Data">
                        <p>
                            Tipa is designed for team-based use. If you are added as a team
                            member by a team (business) owner, that owner and any supervisors
                            on the team can view and manage inventory data associated with the
                            team, and may be able to view your name, role, and activity within
                            the team. If you are a team owner, you are responsible for the
                            accuracy of the business information you provide and for managing
                            the access you grant to team members.
                        </p>
                    </Section>

                    <Section id="retention" title="7. Data Retention">
                        <p>
                            We retain your account and business data for as long as your
                            account remains active, or as needed to provide the Service. If you
                            delete your account, we will delete or anonymize your personal
                            information within a reasonable period, and that deletion is
                            permanent — the data cannot be recovered afterwards (see
                            {' '}
                            <a href="#account-deletion" className="text-primary underline-offset-4 hover:underline">
                                Section 5
                            </a>
                            ). The only exceptions are records we are required to retain to
                            comply with legal, tax, accounting, or security obligations, or to
                            resolve disputes. Short-lived
                            verification codes and OTPs are discarded shortly after use or
                            expiry. Diagnostic/crash logs are retained only as long as needed
                            for troubleshooting purposes.
                        </p>
                    </Section>

                    <Section id="security" title="8. Data Security">
                        <p>
                            We use industry-standard safeguards to protect your information,
                            including password hashing, token-based authentication (JWT),
                            encrypted connections (HTTPS/TLS) between the app and our servers,
                            and access controls on our infrastructure. No method of
                            transmission or storage is 100% secure, and we cannot guarantee
                            absolute security, but we work to protect your information and to
                            respond promptly to any suspected incident.
                        </p>
                    </Section>

                    <Section id="rights" title="9. Your Rights and Choices">
                        <p>Depending on your location, you may have the right to:</p>
                        <ul className="list-disc space-y-1.5 pl-5">
                            <li>
                                Access, review, and correct your personal information through
                                your profile settings in the app
                            </li>
                            <li>Request a copy or export of your data</li>
                            <li>
                                Request permanent deletion of your account and associated
                                personal data, which cannot be reversed once completed
                            </li>
                            <li>
                                Opt out of promotional emails at any time, either via the
                                unsubscribe link in the email or in-app settings
                            </li>
                            <li>
                                Disable push notifications at any time from your device or
                                in-app settings
                            </li>
                            <li>
                                Withdraw consent for optional processing, where consent is the
                                basis for that processing
                            </li>
                        </ul>
                        <p>
                            To exercise any of these rights, contact us using the details in
                            {' '}
                            <a href="#contact" className="text-primary underline-offset-4 hover:underline">
                                Section 14
                            </a>
                            . We may need to verify your identity before fulfilling certain
                            requests.
                        </p>
                    </Section>

                    <Section id="children" title="10. Children's Privacy">
                        <p>
                            Tipa is intended for business use by adults and is not directed at
                            children under 13 (or the minimum age required by your local law).
                            We do not knowingly collect personal information from children. If
                            you believe a child has provided us with personal information,
                            please contact us so we can delete it.
                        </p>
                    </Section>

                    <Section id="transfers" title="11. International Data Transfers">
                        <p>
                            Our servers and service providers may be located in a country other
                            than your own. By using the Service, you understand that your
                            information may be transferred to, stored, and processed in a
                            jurisdiction that may have different data protection laws than your
                            country of residence. We take steps to ensure your data continues
                            to be protected in line with this policy wherever it is processed.
                        </p>
                    </Section>

                    <Section id="thirdparty" title="12. Third-Party Services">
                        <Card>
                            <p className="text-[0.95rem] leading-relaxed text-muted-foreground">
                                The Service relies on the following categories of third-party
                                providers:
                            </p>
                            <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[0.95rem] leading-relaxed text-muted-foreground">
                                <li>
                                    <span className="font-medium text-foreground">Cloud/object storage</span>
                                    {' '}
                                    — for storing profile and product images and other uploaded
                                    files
                                </li>
                                <li>
                                    <span className="font-medium text-foreground">Push notification providers</span>
                                    {' '}
                                    — to deliver optional app notifications, where enabled
                                </li>
                                <li>
                                    <span className="font-medium text-foreground">Email delivery providers</span>
                                    {' '}
                                    — to send verification codes, OTPs, and account notices
                                </li>
                            </ul>
                            <p className="mt-3 text-[0.95rem] leading-relaxed text-muted-foreground">
                                These providers only receive the information necessary to
                                perform their function and are not permitted to use it for
                                their own purposes.
                            </p>
                        </Card>
                    </Section>

                    <Section id="changes" title="13. Changes to This Policy">
                        <p>
                            We may update this Privacy Policy from time to time to reflect
                            changes to the Service or applicable law. We will update the &quot;Last
                            updated&quot; date at the top of this page, and where changes are
                            material, we will provide additional notice (such as an in-app
                            notice or email). Your continued use of the Service after changes
                            take effect constitutes acceptance of the updated policy.
                        </p>
                    </Section>

                    <Section id="contact" title="14. Contact Us">
                        <p>
                            If you have questions, concerns, or requests regarding this Privacy
                            Policy or your personal information, please contact us at:
                        </p>
                        <Card className="text-[0.95rem] text-muted-foreground">
                            <p className="font-medium text-foreground">Code X Pvt Ltd</p>
                            <p className="mt-1">
                                Email:
                                {' '}
                                <a
                                    href="mailto:codexpokhara@gmail.com"
                                    className="text-primary underline-offset-4 hover:underline"
                                >
                                    codexpokhara@gmail.com
                                </a>
                            </p>
                        </Card>
                    </Section>
                </div>

                <footer className="mt-14 border-t border-border/70 pt-6 text-xs text-muted-foreground">
                    © 2026 Code X Pvt Ltd. All rights reserved. This page is the privacy
                    policy for the Tipa application.
                </footer>
            </main>
        </div>
    );
}

export default PrivacyPolicy;
